import React, { type ComponentType } from 'react';

import type { SxProps, Theme } from '@mui/material/styles';

import theme from 'shared/themes/default';
import Button from 'shared/atoms/Button';
import { getUserInitials } from 'shared/utils';
import type { BackgroundThemes } from 'shared/const';
import type { ButtonType } from 'shared/atoms/Button/const';
import type { IconButtonType } from './const';
import { ICONBUTTON_TYPE_ENUM } from './const';

interface IconButtonUser {
  firstName?: string;
  lastName?: string;
  name?: string;
  [key: string]: unknown;
}

interface IconButtonProps extends Omit<
  React.HTMLAttributes<HTMLButtonElement>,
  'color' | 'children'
> {
  ariaLabel: string;
  disabled?: boolean;
  icon?: ComponentType<{ sx?: SxProps<Theme> }>;
  small?: boolean;
  sx?: SxProps<Theme>;
  sxIcon?: SxProps<Theme>;
  theme?: BackgroundThemes;
  type?: IconButtonType;
  user?: IconButtonUser;
}

const IconButton = ({
  ariaLabel,
  icon: Icon,
  small,
  sx: sxButton = {},
  sxIcon = {},
  theme: themeLayout = 'dark',
  type = 'primary',
  user,
  ...props
}: IconButtonProps) => {
  const sxGeneric: SxProps<Theme> = {
    padding: small ? '0.125rem' : '0.5rem',
    minWidth: 'unset',
  };

  let fontSize = '24px';
  let iconType: ButtonType = 'primary';
  let sxType: SxProps<Theme> = {};
  let sxUser: SxProps<Theme> = {};

  if (user) {
    if (small) {
      sxUser = {
        width: '28px',
        height: '28px',
      };
    } else {
      sxUser = {
        width: '40px',
        height: '40px',
      };
    }
  }

  switch (type) {
    case 'secondary':
      iconType = 'secondaryOutline';
      break;
    case 'secondaryAlt':
      if (themeLayout === 'light') {
        sxType = {
          background: theme.palette.darkWrapper,
          color: theme.palette.lightPure,
          '&:active': {
            color: theme.palette.brand,
          },
          '&:hover': {
            background: theme.palette.darkDistinct,
          },
          '&.Mui-disabled': {
            background: theme.palette.disabledDark,
            color: theme.palette.disabledLight,
          },
        };
        iconType = 'primary';
      } else {
        iconType = 'secondary';
      }
      break;
    case 'transparent':
      sxType = {
        background: 'transparent',
        color:
          themeLayout === 'dark'
            ? theme.palette.lightPure
            : theme.palette.mainDark,
        '&:active': {
          background: `${
            themeLayout === 'dark'
              ? theme.palette.lightPure
              : theme.palette.pureDark
          }30`,
          color:
            themeLayout === 'dark'
              ? theme.palette.brand
              : theme.palette.mainDark,
        },
        '&:hover': {
          background: `${
            themeLayout === 'dark'
              ? theme.palette.lightPure
              : theme.palette.pureDark
          }17`,
        },
        '&.Mui-disabled': {
          color: `${
            themeLayout === 'dark'
              ? theme.palette.lightPure
              : theme.palette.pureDark
          }53`,
        },
      };
      iconType = 'primary';
      break;
    case 'icon':
      sxType = {
        background: 'transparent',
        color:
          themeLayout === 'dark'
            ? theme.palette.lightPure
            : theme.palette.mainDark,
        padding: small ? '0.125rem' : '0.25rem',
        '&:active': {
          color:
            themeLayout === 'dark'
              ? theme.palette.mainDark
              : theme.palette.lightPure,
        },
        '&:hover': {
          color:
            themeLayout === 'dark'
              ? theme.palette.brand
              : theme.palette.disabledDark,
          background: 'transparent',
        },
        '&.Mui-disabled': {
          background: 'transparent',
          color:
            themeLayout === 'dark'
              ? theme.palette.disabledDark
              : theme.palette.disabledLight,
        },
      };
      fontSize = small ? '24px' : '32px';
      iconType = 'primary';
      break;
    default:
      sxType = {};
      iconType = type as ButtonType;
      break;
  }

  const sx: SxProps<Theme> = {
    ...sxGeneric,
    ...sxType,
    ...sxUser,
    ...sxButton,
  };

  return (
    <Button
      small={small}
      sx={sx}
      type={iconType}
      theme={themeLayout}
      ariaLabel={ariaLabel}
      {...props}
    >
      {user && !Icon
        ? getUserInitials(user)
        : Icon && <Icon sx={{ fontSize, ...sxIcon }} />}
    </Button>
  );
};

export default IconButton;
export type { IconButtonProps };
export { ICONBUTTON_TYPE_ENUM };
